/**
 * Schémas Zod — Users (gestion membres du tenant).
 *
 * Les courtiers ont leur propre flow (`/v1/brokers` + `broker_profiles`).
 * Cette route /v1/users couvre :
 *   - super_admin (cross-tenant, sans tenant_id)
 *   - tenant_admin (admin du cabinet)
 *   - client_contact (contact côté entreprise cliente — typiquement RH)
 *
 * Note sur les rôles invitables :
 * --------------------------------
 *  - super_admin → peut créer N'IMPORTE QUEL rôle (y compris super_admin)
 *  - tenant_admin → ne peut créer QUE des rôles non-admin (broker, client_contact)
 *
 * Le schéma Zod accepte tous les rôles ; le contrôle d'escalade se fait dans
 * la route (cf. routes/users.ts → assertCanAssignRole).
 */

import { z } from "zod";

import { USER_ROLES } from "../constants.js";

import { EmailSchema, LocaleSchema, PhoneSchema } from "./common.js";

// -----------------------------------------------------------------------------
// Invitation — crée un user via Supabase Auth puis insère dans app.users
// -----------------------------------------------------------------------------
export const InviteUserSchema = z.object({
  email: EmailSchema,
  full_name: z.string().min(2).max(120),
  role: z.enum(USER_ROLES),
  locale: LocaleSchema,
  phone: PhoneSchema.optional(),
  // Override tenant — utilisable UNIQUEMENT par un super_admin (sinon ignoré
  // côté route, le tenant courant prend la priorité).
  tenant_id: z.string().uuid().nullable().optional(),
  // Message custom dans l'email d'invitation (ajouté aux métadonnées Auth).
  message: z.string().max(500).optional(),
});
export type InviteUserInput = z.infer<typeof InviteUserSchema>;

// -----------------------------------------------------------------------------
// Update — profil + rôle / statut
// -----------------------------------------------------------------------------
export const UpdateUserSchema = z.object({
  full_name: z.string().min(2).max(120).optional(),
  phone: PhoneSchema.nullable().optional(),
  locale: LocaleSchema.optional(),
  avatar_url: z.string().url().nullable().optional(),
});
export type UpdateUserInput = z.infer<typeof UpdateUserSchema>;

// Update rôle/statut — séparé pour appliquer des contrôles spécifiques.
export const UpdateUserRoleSchema = z.object({
  role: z.enum(USER_ROLES).optional(),
  status: z.enum(["active", "pending", "disabled"]).optional(),
});
export type UpdateUserRoleInput = z.infer<typeof UpdateUserRoleSchema>;

// -----------------------------------------------------------------------------
// Filtre de listing
// -----------------------------------------------------------------------------
export const UserFilterSchema = z.object({
  role: z.enum([...USER_ROLES, "all", "non_broker"]).default("non_broker"),
  status: z.enum(["active", "pending", "disabled", "all"]).default("all"),
  q: z.string().max(100).optional(),
  include_deleted: z.coerce.boolean().default(false),
});
export type UserFilter = z.infer<typeof UserFilterSchema>;

/** Explicit human acceptance of one invitation; selecting a cabinet is not acceptance. */
export const AcceptMembershipSchema = z
  .object({
    tenant_id: z.string().uuid(),
    role: z.enum(["tenant_admin", "broker", "client_contact"]),
  })
  .strict();
export const MembershipSchema = z.object({
  tenant_id: z.string().uuid(),
  tenant_name: z.string(),
  role: z.enum(["tenant_admin", "broker", "client_contact"]),
  status: z.enum(["active", "pending", "disabled"]),
  is_default: z.boolean(),
  invited_at: z.string().nullable(),
  accepted_at: z.string().nullable(),
});
export type Membership = z.infer<typeof MembershipSchema>;

/** Administrative, tenant-scoped delivery metadata. Never includes links or message bodies. */
export const MemberInvitationEventSchema = z.object({
  created_at: z.string(),
  sent_at: z.string().nullable(),
  status: z.enum(["queued", "sent", "failed", "expired"]),
  attempts: z.number().int().nonnegative(),
  delivery_status: z
    .enum(["delivered", "opened", "clicked", "bounced", "complained", "failed"])
    .nullable(),
  delivery_updated_at: z.string().nullable(),
});
export const MemberInvitationHistorySchema = z.object({
  events: z.array(MemberInvitationEventSchema),
  truncated: z.boolean(),
});
